/**
 * Runtime CRM env override helpers.
 *
 * Use case: flip between prod / staging / custom CRM endpoints during UAT
 * without restarting or rebuilding the frontend. Override persists in
 * localStorage so it survives refresh, and a visible badge on-screen
 * (EnvBadge) makes sure nobody confuses envs.
 *
 * URL grammar:
 *   ?crm_url=<https://...>&crm_key=<wds_...>&crm_env=<label>  → set + persist
 *   ?crm=reset  (or ?crm=prod)                                → clear override
 *
 * Precedence used by crmApi.js at request time:
 *   1. localStorage override      (set via URL param above or manually)
 *   2. process.env REACT_APP_*    (compile-time defaults, i.e. prod)
 */

const LS_URL = 'wd_crm_url';
const LS_KEY = 'wd_crm_key';
const LS_ENV = 'wd_crm_env';

const safeLS = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

export function getCrmOverride() {
  const ls = safeLS();
  if (!ls) return null;
  const url = ls.getItem(LS_URL);
  const key = ls.getItem(LS_KEY);
  if (url && key) {
    return { url, key, env: ls.getItem(LS_ENV) || 'custom' };
  }
  return null;
}

export function setCrmOverride({ url, key, env }) {
  const ls = safeLS();
  if (!ls || !url || !key) return;
  ls.setItem(LS_URL, url);
  ls.setItem(LS_KEY, key);
  ls.setItem(LS_ENV, env || 'custom');
  try {
    window.dispatchEvent(new Event('wd-crm-env-changed'));
  } catch {
    /* noop */
  }
}

export function clearCrmOverride() {
  const ls = safeLS();
  if (!ls) return;
  ls.removeItem(LS_URL);
  ls.removeItem(LS_KEY);
  ls.removeItem(LS_ENV);
  try {
    window.dispatchEvent(new Event('wd-crm-env-changed'));
  } catch {
    /* noop */
  }
}

/**
 * Reads ?crm_url=... / ?crm_key=... / ?crm=reset from the URL (if present)
 * and writes them to localStorage. Call once at app boot.
 * Removes the CRM params from the URL after consuming them so links don't
 * leak site keys in browser history.
 */
export function applyUrlOverride() {
  if (typeof window === 'undefined') return null;

  const search = window.location.search;
  if (!search) return getCrmOverride();

  const params = new URLSearchParams(search);
  const action = (params.get('crm') || '').toLowerCase();
  const url = params.get('crm_url');
  const key = params.get('crm_key');
  const env = params.get('crm_env') || 'staging';

  let changed = false;

  if (action === 'reset' || action === 'prod') {
    clearCrmOverride();
    changed = true;
  } else if (url && key) {
    setCrmOverride({ url, key, env });
    changed = true;
  }

  if (changed) {
    // Strip the CRM params from the URL bar
    params.delete('crm');
    params.delete('crm_url');
    params.delete('crm_key');
    params.delete('crm_env');
    const newSearch = params.toString();
    const cleanUrl =
      window.location.pathname +
      (newSearch ? `?${newSearch}` : '') +
      window.location.hash;
    try {
      window.history.replaceState({}, '', cleanUrl);
    } catch {
      /* noop */
    }
  }

  return getCrmOverride();
}
